import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { default as MUIBottomNavigation } from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'

import Banner from '../Banner'
import styles from './bottomNavigation.styles'
import type { NotificationConfiguration } from '../../../types/domain'
import type { NotificationType } from '../../../types/model'

const MOBILE_BOTTOM_NAVIGATION_ID = 'mobile-bottom-navigation'

export const getNavButtonId = <T extends string>(navName: T) =>
  `${navName}-nav-button`

interface NavigationAction<NavigationItem> {
  name: NavigationItem
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  disabled: boolean
}

const NavIcon = (
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
): React.ReactElement => <Icon height='24px' width='24px' />

const getActionIndexOnName = <NavItem,>(
  actions: NavigationAction<NavItem>[],
  actionName: NavItem,
): number | undefined => {
  const i = actions.findIndex((a) => a.name === actionName)
  return i === -1 ? undefined : i
}

interface MobileBottomNavigationProps<NavItem> {
  actions: NavigationAction<NavItem>[]
  activeItem?: NavItem
  onClickNavItem: (newNavItem: NavItem) => void
  banner?: NotificationConfiguration
  onClickBanner?: (notificationType: NotificationType) => void
  onShowBanner?: (notificationType: NotificationType) => void
}

const BottomNavigation = <NavItem,>({
  actions,
  activeItem,
  onClickNavItem,
  banner,
  onClickBanner = () => {},
  onShowBanner = () => {},
}: MobileBottomNavigationProps<NavItem>) => {
  const readingBarRef = useRef<HTMLDivElement>(null)

  const [bannerIsVisible, setBannerIsVisible] = useState(!!banner)

  const handleBannerClick = () => {
    setBannerIsVisible(false)
    onClickBanner((banner as NotificationConfiguration).name)
  }

  useEffect(() => {
    setBannerIsVisible(!!banner)
  }, [banner])

  return (
    <>
      <Box sx={styles.fixed}>
        <Banner
          containerRef={readingBarRef.current}
          isVisible={bannerIsVisible}
          message={banner?.config.message}
          iconButton={banner?.config?.iconButton}
          onClick={handleBannerClick}
          onShow={() =>
            onShowBanner((banner as NotificationConfiguration).name)
          }
        />
        <Box id={MOBILE_BOTTOM_NAVIGATION_ID} sx={styles.root}>
          <MUIBottomNavigation
            ref={readingBarRef}
            showLabels
            value={getActionIndexOnName(actions, activeItem)}
            onChange={(_, newNavIndex) => {
              const selectedAction = actions[newNavIndex]
              if (selectedAction) {
                onClickNavItem(selectedAction.name)
              }
            }}
            sx={styles.navigation}
          >
            {actions.map((action) => (
              <BottomNavigationAction
                key={getNavButtonId(action.name as string)}
                id={getNavButtonId(action.name as string)}
                label={action.label}
                icon={NavIcon(action.icon)}
                disabled={action.disabled}
              />
            ))}
          </MUIBottomNavigation>
        </Box>
      </Box>
    </>
  )
}

export default BottomNavigation
